import type {
  DisciplineDemoDetails,
  DisciplineReportGoalsRow,
  DisciplineReportItem,
} from './discipline-report'

import { useListsStore } from '@shared/model'

import { useDisciplineStore } from './store'
import {
  buildDemoDetails,
  buildDemoKidInfo,
  buildDemoParentInfo,
  buildDisciplineGoalsRows,
} from './discipline-report'

interface DisciplineReportReturn {
  refPadUrl: ComputedRef<string | null>
  refParentInfo: ComputedRef<DisciplineReportItem[]>
  refKidInfo: ComputedRef<DisciplineReportItem[]>
  refDemoDetails: ComputedRef<DisciplineDemoDetails>
  refGoalsRows: ComputedRef<DisciplineReportGoalsRow[]>
}

export const useDisciplineReport = (): DisciplineReportReturn => {
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()

  const refDetails = computed(() => disciplineStore.getDetails)
  const refFeedback = computed(() => refDetails.value?.latest_demo_booking?.tutor_feedback ?? null)

  const refPadUrl = computed<string | null>(() => refDetails.value?.links.pad_url ?? null)

  const refParentInfo = computed<DisciplineReportItem[]>(() => buildDemoParentInfo(refFeedback.value))

  const refKidInfo = computed<DisciplineReportItem[]>(() => buildDemoKidInfo(refFeedback.value, {
    genders: listsStore.getLists?.genders ?? {},
    grades: listsStore.getLists?.kid.grade ?? {},
  }))

  const refDemoDetails = computed<DisciplineDemoDetails>(() =>
    buildDemoDetails(refDetails.value?.code ?? '', refFeedback.value))

  const refGoalsRows = computed<DisciplineReportGoalsRow[]>(() => {
    const activation = listsStore.getLists?.activation

    return buildDisciplineGoalsRows(refDetails.value?.activation_data.details ?? null, {
      mainGoal: activation?.expectations.activation_goals ?? {},
      advancedGoal: activation?.advanced_goal ?? {},
      focusSkill: activation?.focus_skill?.all ?? {},
      gradeLevel: activation?.grade_level ?? {},
      homeschoolInvolvement: activation?.homeschool_involvement ?? {},
      gradesMatter: activation?.grades_matter ?? {},
      learningNeed: activation?.learning_needs ?? {},
      childName: refDetails.value?.kid.name ?? '',
    })
  })

  return {
    refPadUrl,
    refParentInfo,
    refKidInfo,
    refDemoDetails,
    refGoalsRows,
  }
}
